import { DOCUMENT } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, InjectionToken, Injector, input, OnDestroy, runInInjectionContext, signal, ViewEncapsulation } from '@angular/core';
import { ICellRendererAngularComp } from 'ag-grid-angular';
import { GridApi, ICellRendererParams, IRowNode } from 'ag-grid-enterprise';
import { Entity, outlineCrewEntities } from '@jms/model';
import { ScheduleEngine } from '@jms/model/scheduling/schedule.engine';
import { TooltipModule } from 'primeng/tooltip';
import { AvatarMode } from '@workbuddy/ui/components/avatar';
import { BadgeComponent, BadgeMode } from '@workbuddy/ui/components/badge';
import { resolveBoardParams } from '@workbuddy/ui/v2/blocks/entity.board/context';
import { EntityBoardStore } from '@workbuddy/ui/v2/blocks/entity.board/state';
import { EntityDialogs } from '@workbuddy/ui/v2/blocks/entity.dialog';
import { ButtonIconComponent } from '@workbuddy/ui/v2/components/button';
import { ContentComponent, ContentModel } from '@workbuddy/ui/v2/components/primitives/content';
import { CheckboxComponent } from '@workbuddy/ui/v2/fields/checkbox';
import { DesignSystem } from '@workbuddy/ui/v2/design-system';
import { commandKeymap, formatChord, SemanticCommand } from '@workbuddy/ui/services/keyboard.shortcuts';
import { OUTLINE_ENTITY_KINDS, OutlineEntityKinds, OutlineEntityKindsParams, OutlineRow } from '../outline.authoring.model';
import { openOutlineChildCreate, resolveOutlineCreateParent } from '../outline.create';
import { OutlineRevealApi, revealOutlineChild } from '../outline.reveal';
import { isOutlineServiceLine, isOutlineTask } from '../outline.selection';
import { outlineScheduleChipLabel } from './outline.schedule.chip';
import { outlineLineWindowChip } from './outline.window.chip';
import { outlineScheduleCard, OutlineScheduleCard, OutlineSchedulePlanContext } from './outline.schedule.tooltip';
import { outlineRowBridgeStyles, outlineRowStyleId } from './outline.row.renderer.theme';

/** Stateless and pure — one instance serves every rendered row. */
const SCHEDULE_ENGINE = new ScheduleEngine();

export const OUTLINE_SCHEDULE_TODAY = new InjectionToken<() => string>('OUTLINE_SCHEDULE_TODAY', { providedIn: 'root', factory: () => outlineToday });

function outlineToday(): string {

  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
}

interface OutlineProperty {
  id: string;
  label: string;
  tooltip?: string;
  card?: OutlineScheduleCard;
  iconLeft?: string;
  iconLeftColor?: string;
}

@Component({
  selector: 'outline-row',
  standalone: true,
  templateUrl: './outline.row.renderer.html',
  styleUrls: ['./outline.row.renderer.less'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  // The host element IS the row — level and selection classes live here, not on a wrapper div.
  host: { '[class.service-line]': '$isServiceLineRow()', '[class.task]': '$isTaskRow()', '[class.subtask]': '$isSubtaskRow()', '[class.select-on]': '$selectOn()' },
  imports: [BadgeComponent, ButtonIconComponent, CheckboxComponent, ContentComponent, TooltipModule],
})
export class OutlineRowRendererComponent implements ICellRendererAngularComp, OnDestroy {

  /** Readonly projection, independent of AG Grid row ownership. Omit for the live authoring renderer. */
  readonly presentationRow = input<OutlineRow>();
  /** Null omits the authoring-kind glyph when a readonly projection has no matching entity icon. */
  readonly presentationIcon = input<string | null>();
  /** Flat readonly rows can omit the empty expander lane; real tree rows keep their alignment. */
  readonly reserveTreeGutter = input(true);
  private readonly document = inject(DOCUMENT);
  private readonly dialogs = inject(EntityDialogs);
  private readonly injector = inject(Injector);
  private readonly store = inject(EntityBoardStore, { optional: true });
  private readonly today = inject(OUTLINE_SCHEDULE_TODAY);
  /**
   * AG Grid builds cell renderers from the grid's own ViewContainerRef, so a relation builder cannot
   * put a per-mount provider on this injector. The mount's kinds therefore ride the board `params`
   * bag and win; the token is the root default and the seam tests override.
   */
  private readonly injectedKinds = inject(OUTLINE_ENTITY_KINDS, { optional: true });
  protected readonly $kinds = computed<OutlineEntityKinds | null>(() => resolveBoardParams<OutlineEntityKindsParams>(this.store?.$context?.())?.outlineEntityKinds ?? this.injectedKinds);
  private readonly gridRow = signal<OutlineRow | null>(null);
  protected readonly $row = computed(() => this.presentationRow() ?? this.gridRow());
  protected readonly $expanded = signal(false);
  protected readonly $readonly = computed(() => {

    const context = this.store?.$context?.();
    return this.presentationRow() !== undefined || context?.readonly === true || resolveBoardParams<{ readonly?: boolean }>(context)?.readonly === true;
  });
  protected readonly $selectSlot = computed(() => !this.$readonly() && this.store?.$selection?.()?.type === 'multiple');
  protected readonly $canAddTasksFromChecklist = computed(() => {

    const row = this.$row();
    const params = resolveBoardParams<OutlineEntityKindsParams>(this.store?.$context?.());
    return !!row && this.canAddChild(row) && typeof params?.addTasksFromChecklist === 'function';
  });
  protected readonly $selectable = computed(() => {

    const kinds = this.$kinds();
    return !!kinds && this.$selectSlot() && (isOutlineTask(this.$row(), kinds) || isOutlineServiceLine(this.$row(), kinds));
  });
  protected readonly $selected = signal(false);
  protected readonly $indeterminate = signal(false);
  protected readonly $selectOn = computed(() => this.$selected() || this.$indeterminate());
  protected readonly $isServiceLineRow = computed(() => this.isLine(this.$row()));
  protected readonly $isSubtaskRow = computed(() => {
    const row = this.$row();
    return !!row && this.isSubtask(row);
  });
  protected readonly $isTaskRow = computed(() => {
    const row = this.$row();
    return !!row && !this.isLine(row) && !this.isSubtask(row);
  });
  protected readonly BadgeMode = BadgeMode;
  protected readonly $showDescription = computed(() => !this.store || (this.store.$effectiveView()?.list?.fields ?? []).includes('description'));
  protected readonly $crew = computed<ContentModel | null>(() => {
    const row = this.$row();
    if (!this.isLine(row)) return null;
    if (!this.store || !(this.store.$effectiveView()?.list?.fields ?? []).includes('defaultResources')) return null;
    const avatar = outlineCrewEntities((row as { defaultContractor?: Entity }).defaultContractor, row?.defaultResources).map(resource => {
      const label = outlineCrewLabel(resource);
      return { value: label, mode: AvatarMode.Letter, tooltip: label };
    });
    return avatar.length ? { label: null, avatar, mode: 'item', labelMode: 'text', growLabel: false } : null;
  });
  /** A line nobody is booked to. Opt-in per mount, because only the live plan staffs Jobs from it. */
  protected readonly $noCrew = computed(() => {
    const row = this.$row();
    if (!this.isLine(row) || resolveBoardParams<OutlineEntityKindsParams>(this.store?.$context?.())?.flagNoCrew !== true) return false;
    return !outlineCrewEntities((row as { defaultContractor?: Entity }).defaultContractor, row?.defaultResources).length;
  });
  protected readonly $properties = computed<OutlineProperty[]>(() => {
    const row = this.$row();
    if (!row || !this.store) return [];
    return (this.store.$effectiveView()?.list?.fields ?? [])
      .filter(id => id !== 'description' && id !== 'defaultResources')
      // Visit outlines already carry canonical evidence badges. Suppress only that same semantic
      // property, never a label match: different requirements may legitimately share display text.
      .filter(id => id !== 'evidence' || !row.badges?.some(badge => badge.id === 'evidence'))
      .map(id => this.resolveProperty(row, id))
      .filter((property): property is OutlineProperty => property != null);
  });
  protected readonly $scheduleCard = computed(() => this.$properties().find(property => property.card)?.card ?? null);
  private params: ICellRendererParams<OutlineRow> | null = null;
  private listenedNode: IRowNode | null = null;
  private listenedApi: GridApi | null = null;
  // Grid expansion can change without a cell refresh (lazy loads, purges), so the node event is the source of truth.
  private readonly onExpandedChanged = () => this.$expanded.set(this.params?.node?.expanded === true);
  private readonly onSelectionChanged = () => this.syncSelection();

  public constructor() {

    DesignSystem.Mount(this.document, outlineRowStyleId, outlineRowBridgeStyles);
  }

  public agInit(params: ICellRendererParams<OutlineRow>): void {

    this.attach(params);
  }

  public refresh(params: ICellRendererParams<OutlineRow>): boolean {

    this.attach(params);
    return true;
  }

  public ngOnDestroy(): void {

    this.listenedNode?.removeEventListener?.('expandedChanged', this.onExpandedChanged);
    this.listenedNode = null;
    this.listenedApi?.removeEventListener?.('selectionChanged', this.onSelectionChanged);
    this.listenedApi?.removeEventListener?.('modelUpdated', this.onSelectionChanged);
    this.listenedApi = null;
  }

  private attach(params: ICellRendererParams<OutlineRow>): void {

    this.params = params;
    if (this.listenedNode !== params.node) {
      this.listenedNode?.removeEventListener?.('expandedChanged', this.onExpandedChanged);
      this.listenedNode = params.node ?? null;
      this.listenedNode?.addEventListener?.('expandedChanged', this.onExpandedChanged);
    }
    if (this.listenedApi !== params.api) {
      this.listenedApi?.removeEventListener?.('selectionChanged', this.onSelectionChanged);
      this.listenedApi?.removeEventListener?.('modelUpdated', this.onSelectionChanged);
      this.listenedApi = params.api ?? null;
      this.listenedApi?.addEventListener?.('selectionChanged', this.onSelectionChanged);
      this.listenedApi?.addEventListener?.('modelUpdated', this.onSelectionChanged);
    }
    this.$expanded.set(params.node?.expanded === true);
    this.gridRow.set(params.data ?? null);
    this.syncSelection();
  }

  protected toggleExpanded(event: Event, row: OutlineRow): void {

    event.stopPropagation();
    if (event.currentTarget instanceof HTMLElement) event.currentTarget.blur();
    if (this.presentationRow() !== undefined || !row.hasChildren || !this.params?.node) return;
    const expanded = this.params.node.expanded !== true;
    this.$expanded.set(expanded);
    this.params.node.setExpanded(expanded);
  }

  protected onSelectionToggle(selected: boolean): void {

    const row = this.$row();
    if (!this.$selectable() || !row) return;
    if (!this.params?.node) return;
    // Every checkbox is self-only. Line-level and task-level bulk actions have different semantics,
    // so selecting a line must never silently add its tasks to the selection.
    this.params.node.setSelected(selected);
    this.$selected.set(selected);
    this.$indeterminate.set(false);
  }

  private syncSelection(): void {

    this.$indeterminate.set(false);
    this.$selected.set(this.params?.node?.isSelected?.() === true);
  }

  public addChild(event: Event, row: OutlineRow): void {

    event.stopPropagation();
    if (!this.canAddChild(row)) return;
    const createChild = this.store?.$context?.()?.itemInteraction?.createChild;
    if (createChild) {
      const action = runInInjectionContext(this.injector, () => createChild(row));
      if (action) void action();
      return;
    }
    const api = this.params?.api;
    const kinds = this.$kinds();
    openOutlineChildCreate({
      dialogs: this.dialogs,
      kinds,
      row,
      readonly: this.$readonly(),
       afterSave: async result => {
         const taskId = (result as { _id?: string } | null)?._id;
         if (!taskId || !kinds || !api) return;
         await resolveBoardParams<OutlineEntityKindsParams>(this.store?.$context?.())?.reloadOutline?.();
         await revealOutlineChild({ api: api as unknown as OutlineRevealApi, parentRowId: row.rowId, childRowId: `${kinds.taskPrefix}${taskId}`, setFocusedItemId: id => this.store?.setFocusedItemId(id) });
       },
    });
  }

  protected addTasksFromChecklist(event: Event, row: OutlineRow): void {

    event.stopPropagation();
    const target = resolveOutlineCreateParent(row, this.$kinds());
    if (!target?.parent || !this.$canAddTasksFromChecklist()) return;
    resolveBoardParams<OutlineEntityKindsParams>(this.store?.$context?.())?.addTasksFromChecklist?.(target.parent, async taskIds => {
      const taskId = taskIds[0];
      const kinds = this.$kinds();
      const api = this.params?.api;
      if (!taskId || !kinds || !api) return;
      await revealOutlineChild({ api: api as unknown as OutlineRevealApi, parentRowId: row.rowId, childRowId: `${kinds.taskPrefix}${taskId}`, setFocusedItemId: id => this.store?.setFocusedItemId(id) });
    });
  }

  protected addTasksFromChecklistTooltip(row: OutlineRow): string {

    const isMac = /Mac|iPod|iPhone|iPad/i.test(this.document.defaultView?.navigator.platform ?? '');
    return `${this.isLine(row) ? 'Add tasks' : 'Add subtasks'} from checklist · ${formatChord(commandKeymap[SemanticCommand.AddFromChecklist], isMac)}`;
  }

  /** The + is the pointer twin of the `N` chord on a focused row, so its tooltip teaches the key. */
  protected addChildTooltip(row: OutlineRow): string {

    const isMac = /Mac|iPod|iPhone|iPad/i.test(this.document.defaultView?.navigator.platform ?? '');
    return `${this.isLine(row) ? 'Add task' : 'Add subtask'} · ${formatChord(commandKeymap[SemanticCommand.Create], isMac)}`;
  }

  protected isLine(row: Pick<OutlineRow, 'sourceEntity'> | null | undefined): boolean {

    const kinds = this.$kinds();
    return !!kinds && isOutlineServiceLine(row, kinds);
  }

  protected isSubtask(row: OutlineRow): boolean {

    const kinds = this.$kinds();
    return !!kinds && isOutlineTask(row, kinds) && row.parentId?.startsWith(kinds.taskPrefix) === true;
  }

  /** Task under a line, or subtask under a task — both take the tree connector. */
  protected isNested(row: OutlineRow): boolean {

    return !!row.parentId;
  }

  /**
   * Whether this row can take a child at all. Shares the policy with {@link openOutlineChildCreate},
   * so the affordance can never offer an add the action would silently refuse.
   */
  protected canAddChild(row: OutlineRow): boolean {

    if (this.$readonly()) return false;
    const createChild = this.store?.$context?.()?.itemInteraction?.createChild;
    if (createChild) return !!runInInjectionContext(this.injector, () => createChild(row));
    return resolveOutlineCreateParent(row, this.$kinds()) != null;
  }

  protected isLastNestedSibling(row: OutlineRow): boolean {

    const node = this.params?.node;
    const rowIndex = node?.rowIndex;
    const api = this.params?.api;
    if (rowIndex == null || !api || !node) return true;
    for (let index = rowIndex + 1;; index += 1) {
      const next = api.getDisplayedRowAtIndex(index);
      if (!next) return true;
      if (next.parent === node.parent) return false;
      if (!isGridAncestor(node, next)) return true;
    }
  }

  /** Chip is the kind (Photo, Signature), never a count — single-evidence is 0 or 1 type. */
  private resolveEvidenceChip(evidence: string[] | undefined): OutlineProperty | null {

    if (!evidence?.length) return null;
    const types: string[] = [];
    for (const kind of evidence) {
      const type = kind.endsWith(' photo') ? 'Photo' : kind;
      if (!types.includes(type)) types.push(type);
    }
    const icons: Record<string, string> = { Signature: 'fa-regular fa-signature', Comment: 'fa-regular fa-comment', File: 'fa-regular fa-file', Form: 'fa-regular fa-clipboard-list', Photo: 'fa-regular fa-camera' };
    return { id: 'evidence', label: types.join(', '), tooltip: evidence.join(', '), iconLeft: types.length === 1 ? icons[types[0]] ?? 'fa-regular fa-camera' : 'fa-regular fa-clipboard-list' };
  }

  private resolveProperty(row: OutlineRow, id: string): OutlineProperty | null {

    const kinds = this.$kinds();
    if (!kinds) return null;
    if (id === 'schedule') {
      if (this.isLine(row) || this.isSubtask(row)) return null;
      // An outline whose rows carry no cadence shows NO schedule chip, tooltip or affordance at all.
      // A Visit is one attendance: there is no rhythm to show, and a chip would invite editing the
      // recurring plan's schedule from a single occurrence. Gated here rather than by leaving
      // `schedule` out of a view's fields, because this chip list comes from the SERVER view — a view
      // that lists the field would otherwise reinstate it. Note this is NOT `datesVisits`: a template
      // has no calendar but does carry a cadence, and keeps its chip.
      if (kinds.hasSchedule === false) return null;
      // Chip carries the compact form so it fits a capped width; the tooltip carries the full
      // summary plus the next due, which is where the ends clause and non-working-day policy remain readable.
      // The one thing the chip adds back is the start override ('· from 15 Sep'): a task starting later
      // than its line is not something the rule alone can say, and it changes what the row means.
      // The engine reads an absent schedule as the working-day default, so no local special case: it
      // answers 'working days' / 'Every working day' for undefined exactly as it does for a real model.
      const parent = this.params?.node?.parent?.data as OutlineRow | undefined;
      const line = this.isLine(parent) ? parent : null;
      const plan = resolveBoardParams<OutlineSchedulePlanContext>(this.store?.$context?.());
      const label = outlineScheduleChipLabel(SCHEDULE_ENGINE.describeShort(row.schedule), row.schedule, row.startsOn);
      return { id, label, card: outlineScheduleCard(row, this.today(), line, plan, kinds.datesVisits ?? true), iconLeft: 'fa-regular fa-calendar' };
    }
    if (id === 'duration' && this.isLine(row)) {
      const chip = outlineLineWindowChip(row.window, row.duration);
      return chip ? { id, label: chip.label, tooltip: chip.tooltip, iconLeft: 'fa-regular fa-clock' } : null;
    }
    if (id === 'items' && this.isLine(row)) {
      const count = row.items ?? 0;
      if (count < 1) return null;
      return { id, label: `${count} item${count === 1 ? '' : 's'}`, tooltip: `Items: ${count}`, iconLeft: 'fa-regular fa-box' };
    }
    if (id === 'evidence' && isOutlineTask(row, kinds)) return this.resolveEvidenceChip(row.evidence);
    return null;
  }

}

function outlineCrewLabel(resource: Entity): string {

  const record = resource as Entity & { firstName?: string; lastName?: string };
  return record.name?.trim() || [record.firstName, record.lastName].filter(Boolean).join(' ').trim() || '?';
}

function isGridAncestor(ancestor: IRowNode, node: IRowNode): boolean {

  for (let parent = node.parent; parent; parent = parent.parent) {
    if (parent === ancestor) return true;
  }
  return false;
}
